// src/accounts/components/TeamMemberFormModal.jsx
//
// Add a Project team member, or edit one's granted roles. Same shape as
// AccountRoleGrantModal.jsx, plus a free-text "role label" (delivery lead,
// tech lead, ...) that's fixed at add time. Replaces EngagementDetailPage's
// old always-open inline form.
//
// size="lg": matches UserFormModal/AccountRoleGrantModal.

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CheckCircle2, XCircle } from "lucide-react";
import { Modal, FormField, Input, Button, Alert, SkeletonText, RolePicker, useDiscardConfirm } from "../../shared/ui";
import UserPicker from "../../components/UserPicker";

function defaultRoleIds(roles) {
  const viewer = roles.find((r) => r.name === "project_viewer");
  return viewer ? [viewer.id] : [];
}

export default function TeamMemberFormModal({
  open,
  member,
  roles,
  rolesLoading,
  rolesError,
  onClose,
  onSubmit,
}) {
  const isEdit = Boolean(member);

  const [userId, setUserId] = useState(member?.userId ?? null);
  const [role, setRole] = useState(member?.role ?? "");
  const [roleIds, setRoleIds] = useState(member?.roleIds ?? defaultRoleIds(roles));
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(null);
  const [dirty, setDirty] = useState(false);

  // project_viewer is checked by default for a new member since
  // AddTeamMemberHandler auto-grants it server-side anyway — this just
  // keeps the form honest about what submitting will actually produce.
  // The catalog can still be loading when the modal first mounts, so
  // re-apply the default once it arrives rather than only computing it
  // once at the initial (possibly empty) render.
  useEffect(() => {
    if (!isEdit && roleIds.length === 0 && roles.length > 0) {
      setRoleIds(defaultRoleIds(roles));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  function handleClose() {
    setError(null);
    setSubmitting(false);
    setSaved(null);
    setDirty(false);
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !saved, handleClose);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await onSubmit({ userId, role: role.trim(), roleIds });
      setSaved(result);
    } catch (err) {
      setError(err?.message || "Save failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        size="lg"
        title={isEdit ? `Edit roles — ${member.username}` : "Add a team member"}
        onClose={requestClose}
      >
        {saved ? (
          <div className="flex flex-col gap-4">
            <Alert
              variant="success"
              icon={CheckCircle2}
              title={isEdit ? `Roles updated for "${saved.username}"` : `"${saved.username}" added to the team`}
            >
              {isEdit
                ? "Their granted roles on this Project now match this selection."
                : "project_viewer is granted automatically alongside anything selected here."}
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            {error && (
              <Alert variant="danger" icon={XCircle} title="Couldn't save">
                {error}
              </Alert>
            )}

            <FormField label="User" required>
              {isEdit ? (
                <p className="text-sm text-ink bg-gray-50 border border-line rounded-card px-3 py-2">
                  {member.username} ({member.email})
                </p>
              ) : (
                <UserPicker
                  value={userId}
                  onChange={(v) => {
                    setUserId(v);
                    setDirty(true);
                  }}
                />
              )}
            </FormField>

            <FormField
              label="Role label"
              required={!isEdit}
              helperText={
                isEdit
                  ? "Can only be set when adding someone — remove and re-add them to change it."
                  : 'A short free-text label, e.g. "delivery lead".'
              }
            >
              <Input
                value={role}
                onChange={(e) => {
                  setRole(e.target.value);
                  setDirty(true);
                }}
                placeholder="e.g. delivery lead"
                disabled={isEdit}
              />
            </FormField>

            <FormField
              label="Granted roles"
              helperText="Saving replaces this member's project-scoped roles with exactly this selection."
            >
              {rolesLoading ? (
                <div className="py-3 px-3 border border-line rounded-card">
                  <SkeletonText lines={4} />
                </div>
              ) : rolesError ? (
                <Alert variant="danger" icon={XCircle} title="Couldn't load assignable roles">
                  {rolesError} Close this dialog and retry.
                </Alert>
              ) : (
                <RolePicker
                  roles={roles}
                  value={roleIds}
                  onChange={(next) => {
                    setRoleIds(next);
                    setDirty(true);
                  }}
                  emptyMessage="No project-level roles found in the catalog yet."
                />
              )}
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                disabled={!userId || (!isEdit && !role.trim()) || rolesLoading || Boolean(rolesError)}
              >
                {isEdit ? "Save changes" : "Add team member"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {confirmDialog}
    </>
  );
}

TeamMemberFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** null → add mode; a member → edit mode, seeded from it. */
  member: PropTypes.shape({
    userId: PropTypes.number,
    username: PropTypes.string,
    email: PropTypes.string,
    role: PropTypes.string,
    roleIds: PropTypes.arrayOf(PropTypes.number),
  }),
  roles: PropTypes.array.isRequired,
  rolesLoading: PropTypes.bool,
  rolesError: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
